// Speil av functions/stateRows.ts — registeret for raddomener (ett dokument per rad).
//
// Et foretak med markøren `rowLayout.domains.sales = 'copied' | 'pruned'` i rotfeltet på
// app/state har fakturaer og betalinger i `businesses/{id}/sales/{id}` og
// `businesses/{id}/payments/{id}` i stedet for i arrayer i app/state
// (docs/architecture/row-per-document-scaling-plan.md). Klienten lytter på radene i
// vinduet (forrige og inneværende regnskapsår, alle åpne fakturaer og deres betalinger) og
// setter dem sammen til `state.invoices` og `state.payments` før sidene ser dem. Radene er
// server-eide; klienten skriver dem aldri (de ligger i SERVER_OWNED_STATE_FIELDS).
//
// REGISTERET MÅ HOLDES I SYNK med functions/stateRows.ts. stateRows.test.js sammenligner
// de rene funksjonene med backend-versjonen.

(function () {
  const ROW_LAYOUT_KEY = 'rowLayout';
  const ROW_LAYOUT_VERSION = 1;
  const ROW_DOMAINS = {
    sales: { keys: { invoices: 'sales', payments: 'payments' } },
    purchases: { keys: { expenses: 'purchases' } },
    bankLines: { keys: { bankLines: 'bankLines' } },
  };
  const ACTIVE_STATUSES = new Set(['copied', 'pruned']);
  // Web-SDK-en (compat) tillater 10 verdier i et `in`-filter.
  const IN_CHUNK = 10;

  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const text = (value) => String(value ?? '').trim();

  const activeRowDomains = (appDocData) => {
    const domains = isPlainObject(appDocData?.[ROW_LAYOUT_KEY]?.domains) ? appDocData[ROW_LAYOUT_KEY].domains : {};
    return Object.keys(ROW_DOMAINS).filter((domain) => ACTIVE_STATUSES.has(domains[domain]));
  };
  const rowDomainStatus = (appDocData, domain) => {
    const status = appDocData?.[ROW_LAYOUT_KEY]?.domains?.[domain];
    return typeof status === 'string' ? status : null;
  };
  const activeRowKeys = (appDocData) => activeRowDomains(appDocData)
    .flatMap((domain) => Object.entries(ROW_DOMAINS[domain].keys).map(([key, collectionId]) => ({ domain, key, collectionId })));

  const CLOSED_INVOICE_STATUSES = new Set(['paid', 'annulled', 'reversed', 'credited', 'expired', 'rejected', 'cancelled', 'lost', 'archived']);
  const rowIsOpen = (key, row) => {
    if (key !== 'bankLines') return ['invoices', 'expenses'].includes(key) && !CLOSED_INVOICE_STATUSES.has(text(row?.status).toLowerCase());
    const line = row?.line || {};
    if (line.reconciliationStatus === 'import-removed' || line.importCorrectionAction === 'removed') return false;
    if (row?.source === 'private') return !['business', 'private', 'ignored'].includes(line.classification);
    if (line.reconciliationStatus === 'approved') return false;
    if (line.reconciliationStatus === 'manual-review' || line.source === 'manual') return true;
    if (line.matchedEntryId) return false;
    return line.classification !== 'private';
  };

  const isoDate = (value) => {
    const raw = text(value);
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const nb = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    return nb ? `${nb[3]}-${nb[2]}-${nb[1]}` : '';
  };

  const rowWindowFor = ({ activeYear, periodFrom, fiscalStart, currentOnly = false }) => {
    const year = Number.isInteger(Number(activeYear)) && Number(activeYear) > 1900 ? Number(activeYear) : new Date().getUTCFullYear();
    const startYear = currentOnly ? year : year - 1;
    const from = fiscalStart(startYear) || `${startYear}-01-01`;
    const period = isoDate(periodFrom);
    return { from: period && period < from ? period : from };
  };

  const rowsToArray = (rows) => [...rows]
    .sort((left, right) => (left.date || '').localeCompare(right.date || '')
      || text(left.row?.createdAt).localeCompare(text(right.row?.createdAt))
      || text(left.row?.id).localeCompare(text(right.row?.id)))
    .map((entry) => entry.row);

  const mergeStateRows = (appState, rowsByKey, servedKeys) => {
    const result = isPlainObject(appState) ? { ...appState } : {};
    for (const key of servedKeys) {
      const rows = Array.isArray(rowsByKey[key]) ? rowsByKey[key] : [];
      if (key !== 'bankLines') { result[key] = rows; continue; }
      const grouped = new Map();
      for (const row of rows) { const group = grouped.get(row.statementId) || []; group.push(row); grouped.set(row.statementId, group); }
      result.bankStatements = (result.bankStatements || []).map((statement) => {
        const lines = (grouped.get(statement.id) || []).sort((a, b) => a.position - b.position).map((entry) => entry.line);
        const { rowSummary, ...header } = statement;
        return { ...header, lines, ...(rowSummary && lines.length < rowSummary.count ? { rowSummary, rowWindowIncomplete: true } : {}) };
      });
    }
    return result;
  };

  const chunk = (values, size) => {
    const chunks = [];
    for (let index = 0; index < values.length; index += size) chunks.push(values.slice(index, index + size));
    return chunks;
  };

  // ---------------------------------------------------------------------------
  // Bare i klienten: lytting (brukes av backend-state-access.jsx).
  //
  // Lytterne for et foretak: fakturaer fra vinduets start, åpne fakturaer, betalinger fra
  // vinduets start, og betalinger til åpne fakturaer eldre enn vinduet (`in`-lyttere, ti
  // fakturaer om gangen, som byttes ut når settet av åpne fakturaer endres). Til alle
  // lyttere har levert sitt første øyeblikksbilde er `ready()` false, slik at et migrert
  // foretak ikke publiseres med tomme arrayer.
  const createRowSubscription = ({ businessRef, isCurrent, onChange, onError, fiscalStart }) => {
    let served = [];
    let currentWindow = null;
    let bankCompact = false;
    const listeners = new Map(); // name -> { unsubscribe, key, seen: Set<id> }
    const arrived = new Set();
    const rows = {}; // key -> Map<id, document>
    let generation = 0;
    let detailGeneration = 0;

    const docsOf = (key) => rows[key] ?? (rows[key] = new Map());
    const detach = (name) => {
      const listener = listeners.get(name);
      if (!listener) return;
      listener.unsubscribe();
      listener.resolveReady(false);
      listeners.delete(name);
      arrived.delete(name);
      for (const id of listener.seen) {
        if (![...listeners.values()].some((other) => other.key === listener.key && other.seen.has(id))) docsOf(listener.key).delete(id);
      }
    };
    const detachAll = () => { [...listeners.keys()].forEach(detach); };
    const attach = (name, key, query) => {
      if (listeners.has(name)) return listeners.get(name).ready;
      const listener = { key, seen: new Set(), unsubscribe: () => {}, resolveReady: () => {}, ready: null };
      listener.ready = new Promise((resolve) => { listener.resolveReady = resolve; });
      listeners.set(name, listener);
      listener.unsubscribe = query.onSnapshot(
        (snapshot) => {
          if (!isCurrent() || listeners.get(name) !== listener) return;
          const map = docsOf(key);
          snapshot.docChanges().forEach((change) => {
            const id = change.doc.id;
            if (change.type === 'removed') {
              listener.seen.delete(id);
              if (![...listeners.values()].some((other) => other !== listener && other.key === key && other.seen.has(id))) map.delete(id);
              return;
            }
            const data = change.doc.data() || {};
            if (isPlainObject(data) && 'row' in data) { map.set(id, data); listener.seen.add(id); }
          });
          arrived.add(name);
          listener.resolveReady(true);
          if (key === 'invoices' || key === 'expenses') refreshOpenPaymentListeners();
          onChange();
        },
        (error) => { listener.resolveReady(false); if (isCurrent() && listeners.get(name) === listener) onError(error); },
      );
      return listener.ready;
    };

    const refreshOpenPaymentListeners = () => {
      const payments = served.find((entry) => entry.key === 'payments');
      if (!payments) return;
      const openIds = [...new Set(['invoices', 'expenses'].flatMap((key) => [...docsOf(key).values()]).filter((doc) => doc.open === true).map((doc) => text(doc.row?.id)).filter(Boolean))].sort();
      const wanted = new Set(chunk(openIds, IN_CHUNK).map((ids) => `payments:open:${ids.join(',')}`));
      for (const name of [...listeners.keys()]) if (name.startsWith('payments:open:') && !wanted.has(name)) detach(name);
      for (const name of wanted) {
        if (listeners.has(name)) continue;
        attach(name, 'payments', businessRef.collection(payments.collectionId).where('documentId', 'in', name.slice('payments:open:'.length).split(',')));
      }
    };

    return {
      // Kalles ved hvert app/state-øyeblikksbilde og når brukeren bytter regnskapsår.
      setLayout(appDocData, appState, journalYear) {
        const nextServed = activeRowKeys(appDocData);
        const nextBankCompact = appDocData?.rowLayout?.clientWindow === 'current' && Array.isArray(appState?.bankStatements)
          && appState.bankStatements.every(statement => statement.rowSummary?.version === 1);
        const start = (year) => fiscalStart(year, appState?.settings);
        const nextWindow = nextServed.length
          ? rowWindowFor({ activeYear: appState?.activeYear, periodFrom: journalYear ? start(Number(journalYear)) || `${journalYear}-01-01` : undefined, fiscalStart: start, currentOnly: appDocData?.rowLayout?.clientWindow === 'current' })
          : null;
        if (JSON.stringify(nextServed) === JSON.stringify(served) && JSON.stringify(nextWindow) === JSON.stringify(currentWindow) && nextBankCompact === bankCompact) return;
        detachAll();
        generation += 1;
        served = nextServed;
        currentWindow = nextWindow;
        bankCompact = nextBankCompact;
        for (const entry of served) {
          const collection = businessRef.collection(entry.collectionId);
          if (entry.key === 'bankLines') {
            if (!bankCompact) {
              attach('bankLines:all', entry.key, collection); continue;
            }
            attach('bankLines:open', entry.key, collection.where('open', '==', true));
          }
          attach(`${entry.key}:dated`, entry.key, collection.where('date', '>=', currentWindow.from));
          if (entry.key === 'invoices' || entry.key === 'expenses') attach(`${entry.key}:open`, entry.key, collection.where('open', '==', true));
        }
      },
      async loadRow(key, id) {
        const entry = served.find((item) => item.key === key);
        if (!entry) return null;
        if (!text(id) || text(id).includes('/')) throw new Error(window.t('common.rowUnavailable'));
        const version = generation;
        const detailVersion = ++detailGeneration;
        const snapshot = await businessRef.collection(entry.collectionId).doc(id).get();
        if (version !== generation || detailVersion !== detailGeneration || !isCurrent()) throw new Error(window.t('common.rowContextChanged'));
        if (!snapshot.exists) throw new Error(window.t('common.rowUnavailable'));
        const document = snapshot.data();
        if (!document?.row) throw new Error(window.t('common.rowUnavailable'));
        const additions = [[key, id, document]];
        const collection = businessRef.collection(entry.collectionId);
        const parentField = key === 'invoices' ? 'creditedFromInvoiceId' : 'supplierCreditForExpenseId';
        const parentId = text(document.row[parentField]);
        if (parentId && parentId !== id) {
          const parent = await collection.doc(parentId).get();
          if (parent.exists) additions.push([key, parent.id, parent.data()]);
        }
        if (['invoices', 'expenses'].includes(key)) {
          const related = await collection.where(`row.${parentField}`, '==', parentId || id).get();
          for (const sibling of related.docs) additions.push([key, sibling.id, sibling.data()]);
        }
        const paymentEntry = served.find((item) => item.key === 'payments');
        if (paymentEntry && ['invoices', 'expenses'].includes(key)) {
          for (const documentId of new Set(additions.map((item) => item[1]))) {
            const payments = await businessRef.collection(paymentEntry.collectionId).where('documentId', '==', documentId).get();
            for (const payment of payments.docs) additions.push(['payments', payment.id, payment.data()]);
          }
        }
        const purchaseEntry = served.find((item) => item.key === 'expenses');
        if (purchaseEntry) {
          const postingIds = [...new Set(additions.filter(item => item[0] === 'payments').map(item => text(item[2].row?.manualPostingId)).filter(Boolean))];
          for (const postingId of postingIds) {
            const posting = await businessRef.collection(purchaseEntry.collectionId).doc(postingId).get();
            if (posting.exists) additions.push(['expenses', posting.id, posting.data()]);
          }
        }
        if (version !== generation || detailVersion !== detailGeneration || !isCurrent()) throw new Error(window.t('common.rowContextChanged'));
        // Keep the detail graph live, including edits and deletions outside the window.
        // One detail context at a time bounds the extra subscriptions.
        for (const name of [...listeners.keys()]) if (name.startsWith('pin:')) detach(name);
        const pinned = new Map();
        for (const [rowKey, rowId, value] of additions) {
          if (!pinned.has(rowKey)) pinned.set(rowKey, new Map());
          pinned.get(rowKey).set(rowId, value);
        }
        const waiting = [];
        for (const [rowKey, documents] of pinned) {
          const rowEntry = served.find(item => item.key === rowKey);
          for (const ids of chunk([...documents.keys()], IN_CHUNK)) waiting.push(attach(`pin:${rowKey}:${ids.join(',')}`, rowKey,
            businessRef.collection(rowEntry.collectionId).where('row.id', 'in', ids)));
        }
        if ((await Promise.all(waiting)).some(ready => !ready) || version !== generation || detailVersion !== detailGeneration || !isCurrent()) throw new Error(window.t('common.rowContextChanged'));
        onChange();
        return document.row;
      },
      async loadBankStatement(statementId) {
        const entry = served.find(item => item.key === 'bankLines');
        if (!entry) return;
        const version = generation;
        for (const name of [...listeners.keys()]) if (name.startsWith('statement:')) detach(name);
        const ready = await attach(`statement:${statementId}`, 'bankLines', businessRef.collection(entry.collectionId).where('statementId', '==', statementId));
        if (!ready || version !== generation || !isCurrent()) throw new Error(window.t('common.rowContextChanged'));
        onChange();
      },
      ready: () => [...listeners.keys()].every((name) => arrived.has(name)),
      // Uten aktive raddomener returneres app/state-objektet urørt.
      merge: (appState) => (served.length
        ? mergeStateRows(appState, Object.fromEntries(served.map((entry) => [entry.key, rowsToArray([...docsOf(entry.key).values()])])), served.map((entry) => entry.key))
        : appState),
      window: () => currentWindow,
      stop() { generation += 1; detachAll(); served = []; currentWindow = null; },
    };
  };

  // Partial mutation responses must preserve unrelated rows and bank lines.
  const mergeMutation = (state, previous, businessId) => {
    const scope = state?.__rowMutation;
    if (!scope) return state;
    if (scope.version !== 1 || scope.businessId !== businessId
      || !['["invoices","payments"]', '["expenses"]', '["expenses","payments"]'].includes(JSON.stringify(scope.keys))) return null;
    const result = { ...state };
    for (const key of ['invoices', 'payments', 'expenses', 'bankStatements']) {
      result[key] = scope.keys.includes(key)
        ? [...new Map([...(previous?.[key] || []), ...(state[key] || [])].map(row => [row.id, row])).values()]
        : previous?.[key];
    }
    delete result.__rowMutation;
    return result;
  };

  window.PlummeStateRows = {
    mergeMutation,
    ROW_LAYOUT_KEY,
    ROW_LAYOUT_VERSION,
    ROW_DOMAINS,
    activeRowDomains,
    rowDomainStatus,
    activeRowKeys,
    rowIsOpen,
    rowWindowFor,
    rowsToArray,
    mergeStateRows,
    chunk,
    createRowSubscription,
  };
})();
